/**
 * 反馈_08 第 8／10 条：交付员「运营商工号」的唯一一份判据（可增行表单 ＋ 读面归一 ＋ 提交载荷）。
 *
 * 模型（T1 §2.4 契约）：一名账号可持**多家运营商**的工号，一运营商至多一枚 ⇒ 行集而非单值。
 *   · 提交：传数组＝全集（数组里缺哪个运营商就删哪一行）；`[]`＝一行都不留（清空）。
 *   · 读面：新键（子表行集）优先；该键缺失（兼容期老回包）才回落旧单值键。
 *
 * 码值口径与后端同源：**全平台唯一一套**运营商字典 `PRODUCT_CARRIER`（四值，含中国广电）。
 * 用户 2026-10-14「运营商的枚举应该共用一套，不要这一套那一套」：本仓旧的三值字典
 * （`CARRIER`／`CARRIER_META`，只有电信／联通／移动、码值形态与后端不同）**已整枚删除**，
 * ⛔ 不许在别处再造第二张运营商字典；读面的历史三值只经 `LEGACY_CARRIER_ALIAS` 做迁移期标签兼容。
 */
export const PRODUCT_CARRIER = {
  CHINA_TELECOM: 'CHINA_TELECOM',
  CHINA_MOBILE: 'CHINA_MOBILE',
  CHINA_UNICOM: 'CHINA_UNICOM',
  CHINA_BROADNET: 'CHINA_BROADNET',
} as const;
export type ProductCarrier = (typeof PRODUCT_CARRIER)[keyof typeof PRODUCT_CARRIER];

/** 展示与提交的固定次序（与后端枚举序一致）：⛔ 不随录入先后抖，脏比对才不会假报改动 */
export const PRODUCT_CARRIER_ORDER: readonly ProductCarrier[] = [
  PRODUCT_CARRIER.CHINA_TELECOM,
  PRODUCT_CARRIER.CHINA_MOBILE,
  PRODUCT_CARRIER.CHINA_UNICOM,
  PRODUCT_CARRIER.CHINA_BROADNET,
];

export const PRODUCT_CARRIER_META: Record<ProductCarrier, { label: string }> = {
  CHINA_TELECOM: { label: '中国电信' },
  CHINA_MOBILE: { label: '中国移动' },
  CHINA_UNICOM: { label: '中国联通' },
  CHINA_BROADNET: { label: '中国广电' },
};

/** 四大运营商的中文名串（提示语用；次序同 PRODUCT_CARRIER_ORDER） */
export const PRODUCT_CARRIER_LABELS = PRODUCT_CARRIER_ORDER.map((code) => PRODUCT_CARRIER_META[code].label).join('、');

/** 工号长度上限（与后端同一枚口径：非空白且不超过 32 个字符） */
export const CARRIER_JOB_NO_MAX = 32;

/** 表单态的一行：运营商未选时用空串（⛔ 不用假运营商码顶替未选态） */
export interface CarrierJobNoRow {
  carrier: ProductCarrier | '';
  jobNo: string;
}

/** 可提交的一行：运营商必为四大之一、工号非空白 */
export interface CarrierJobNoInput {
  carrier: ProductCarrier;
  jobNo: string;
}

export const CARRIER_JOB_NO_ROW_PROBLEM_AT_LEAST_ONE = '请至少录入一组运营商工号：先选运营商，再填工号。';
export const CARRIER_JOB_NO_ROW_PROBLEM_CARRIER = '每一行都要先选运营商（' + PRODUCT_CARRIER_LABELS + '）。';
export const CARRIER_JOB_NO_ROW_PROBLEM_JOB_NO = '每一行都不能只选运营商不填工号：不录的整行请删掉。';
export const CARRIER_JOB_NO_ROW_PROBLEM_TOO_LONG = '运营商工号最长 ' + String(CARRIER_JOB_NO_MAX) + ' 个字符，请缩短后重试。';

export function isProductCarrier(value: unknown): value is ProductCarrier {
  return typeof value === 'string' && PRODUCT_CARRIER_ORDER.some((code) => code === value);
}

export function carrierLabel(carrier: ProductCarrier | ''): string {
  return isProductCarrier(carrier) ? PRODUCT_CARRIER_META[carrier].label : '未选择运营商';
}

export function emptyCarrierJobNoRow(): CarrierJobNoRow {
  return { carrier: '', jobNo: '' };
}

/** 工号真值归一：字符串 trim；数字读面原样转串；其它一律空串（⛔ 不猜） */
export function carrierJobNoText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

/** 整行皆空（运营商没选且工号没填）＝ 用户还没动手的占位行 */
export function isBlankCarrierJobNoRow(row: CarrierJobNoRow): boolean {
  return row.carrier === '' && carrierJobNoText(row.jobNo) === '';
}

export function trimCarrierJobNoRows(rows: readonly CarrierJobNoRow[]): CarrierJobNoRow[] {
  return rows.map((row) => ({ carrier: row.carrier, jobNo: carrierJobNoText(row.jobNo) }));
}

function filledCarrierJobNoRows(rows: readonly CarrierJobNoRow[]): CarrierJobNoRow[] {
  return trimCarrierJobNoRows(rows).filter((row) => !isBlankCarrierJobNoRow(row));
}

/** 提交载荷：丢占位空行、按固定次序排、仅保留值域内的运营商（形状不合规的行由校验器先拦） */
export function carrierJobNoSubmitRows(rows: readonly CarrierJobNoRow[]): CarrierJobNoInput[] {
  const out: CarrierJobNoInput[] = [];
  for (const row of filledCarrierJobNoRows(rows)) {
    if (!isProductCarrier(row.carrier) || row.jobNo === '') continue;
    out.push({ carrier: row.carrier, jobNo: row.jobNo });
  }
  return out.sort((a, b) => PRODUCT_CARRIER_ORDER.indexOf(a.carrier) - PRODUCT_CARRIER_ORDER.indexOf(b.carrier));
}

/** 行集等价（忽略占位空行与录入次序）：脏比对用，⛔ 不拿序列化串比 */
export function sameCarrierJobNoRows(left: readonly CarrierJobNoRow[], right: readonly CarrierJobNoRow[]): boolean {
  const a = carrierJobNoSubmitRows(left);
  const b = carrierJobNoSubmitRows(right);
  if (a.length !== b.length) return false;
  return a.every((row, index) => row.carrier === b[index].carrier && row.jobNo === b[index].jobNo);
}

/**
 * 行集校验：返回空串＝可提交，否则返回一句可直出给操作者的原因。
 *
 * requireAtLeastOne 的三处口径（由调用方按 T1 各条的明确要求传）：
 *   · 平台侧「新建交付员／准入」＝ true（第 8 条：新建必须填至少一组）；
 *   · 平台侧「编辑已绑定档案」＝ 该账号原本就有工号时为 true（不把已发的号一键删空），原本没有时为 false（⛔ 不给老账号凭空加必填）；
 *   · 机构侧「运营商工号」＝ false（第 10 条：删空所有行＝清空该交付员全部运营商工号，弹窗内已明示）。
 * 无论哪一处，「选了运营商却没填工号」的行一律拦下（那才是用户说的「空」）。
 */
export function carrierJobNoRowsProblem(rows: readonly CarrierJobNoRow[], requireAtLeastOne = true): string {
  const filled = filledCarrierJobNoRows(rows);
  if (filled.length === 0) return requireAtLeastOne ? CARRIER_JOB_NO_ROW_PROBLEM_AT_LEAST_ONE : '';
  const seen: ProductCarrier[] = [];
  for (const row of filled) {
    if (!isProductCarrier(row.carrier)) return CARRIER_JOB_NO_ROW_PROBLEM_CARRIER;
    if (row.jobNo === '') return CARRIER_JOB_NO_ROW_PROBLEM_JOB_NO;
    if (row.jobNo.length > CARRIER_JOB_NO_MAX) return CARRIER_JOB_NO_ROW_PROBLEM_TOO_LONG;
    if (seen.includes(row.carrier)) {
      return '同一运营商只能录一个工号（' + PRODUCT_CARRIER_META[row.carrier].label + '重复了），请删掉重复的那一行。';
    }
    seen.push(row.carrier);
  }
  return '';
}

function sortCarrierJobNoRows(rows: readonly CarrierJobNoRow[]): CarrierJobNoRow[] {
  return [...rows].sort((a, b) => {
    const ia = a.carrier === '' ? PRODUCT_CARRIER_ORDER.length : PRODUCT_CARRIER_ORDER.indexOf(a.carrier);
    const ib = b.carrier === '' ? PRODUCT_CARRIER_ORDER.length : PRODUCT_CARRIER_ORDER.indexOf(b.carrier);
    return ia - ib;
  });
}

/**
 * 读面归一（账号档案对象 → 表单行集）：
 *   · 回包带行集键（数组，含空数组）⇒ 只信它（子表为空就是空数组，⛔ 不用旧列伪造一行）；
 *   · 行集键缺失（兼容期老回包）⇒ 回落旧单值键，回落行按电信出（旧列本就是电信口径的单枚）。
 */
export function readCarrierJobNos(source: unknown): CarrierJobNoRow[] {
  if (source === null || typeof source !== 'object') return [];
  const bag = source as { carrierJobNos?: unknown; carrierJobNo?: unknown };
  if (Array.isArray(bag.carrierJobNos)) {
    const rows: CarrierJobNoRow[] = [];
    for (const item of bag.carrierJobNos) {
      if (item === null || typeof item !== 'object') continue;
      const entry = item as { carrier?: unknown; jobNo?: unknown };
      const jobNo = carrierJobNoText(entry.jobNo);
      if (!isProductCarrier(entry.carrier) || jobNo === '') continue;
      rows.push({ carrier: entry.carrier, jobNo });
    }
    return sortCarrierJobNoRows(rows);
  }
  const legacy = carrierJobNoText(bag.carrierJobNo);
  return legacy === '' ? [] : [{ carrier: PRODUCT_CARRIER.CHINA_TELECOM, jobNo: legacy }];
}

/** 竖排展示的一行：`carrier` 保留线上原值（字典外的新枚举码也留着，⛔ 不静默吞行），展示名走 `carrierTextOf` */
export interface CarrierJobNoTextRow {
  carrier: string;
  jobNo: string;
}

/**
 * 展示名（t17）：字典命中出中文名；字典外的枚举码**原样出码**——⛔ 留空、⛔ 冒充「未选择运营商」；
 * 空码＝这一行没选运营商（可增行控件里的占位行）。
 */
export function carrierTextOf(carrier: string): string {
  if (carrier === '') return '未选择运营商';
  return carrierLabelOf(carrier) || carrier;
}

/**
 * **迁移期兼容**（不是第二张字典）：后端切到 ProductCarrier 之前，历史行面可能仍回旧三值
 * `TELECOM`／`UNICOM`／`MOBILE`（本仓已删的那枚旧 CARRIER 字典的三个码）。这里只做「旧码 → 新码」
 * 的一次性别名，中文标签仍出自 `PRODUCT_CARRIER_META`；⛔ 不许在这里另写中文名，⛔ 不许把旧码当提交值发出去。
 * 后端迁完历史数据、读面不再回旧码后可整块删除（删掉后旧码原样出码，不会静默变空白）。
 */
export const LEGACY_CARRIER_ALIAS: Record<string, ProductCarrier> = {
  TELECOM: PRODUCT_CARRIER.CHINA_TELECOM,
  UNICOM: PRODUCT_CARRIER.CHINA_UNICOM,
  MOBILE: PRODUCT_CARRIER.CHINA_MOBILE,
};

/** 读面任意运营商码 → 唯一一套字典里的码（含迁移期旧码）；认不出回 null。 */
export function asProductCarrier(value: unknown): ProductCarrier | null {
  if (typeof value !== 'string') return null;
  const code = value.trim().toUpperCase();
  if (code === '') return null;
  if (isProductCarrier(code)) return code;
  return LEGACY_CARRIER_ALIAS[code] ?? null;
}

/**
 * 运营商展示名：一套字典出中文名，迁移期旧码走 LEGACY_CARRIER_ALIAS；认不出的码**原样出码**
 * （⛔ 不吞、⛔ 不冒充「未选择」）；空值回空串，由调用点自己渲染「—」。
 * 展示面（列表列、详情节）一律走这里，⛔ 别再 import 第二张运营商字典。
 */
export function carrierLabelOf(value: string | null | undefined): string {
  const code = asProductCarrier(value);
  return code === null ? String(value ?? '').trim() : PRODUCT_CARRIER_META[code].label;
}

/** 固定运营商序在前、字典外的码排在后面（同序内保持读面次序，V8 的 sort 是稳定排序） */
function sortCarrierJobNoTextRows(rows: readonly CarrierJobNoTextRow[]): CarrierJobNoTextRow[] {
  const at = (carrier: string): number => {
    const index = PRODUCT_CARRIER_ORDER.indexOf(carrier as ProductCarrier);
    return index < 0 ? PRODUCT_CARRIER_ORDER.length : index;
  };
  return [...rows].sort((a, b) => at(a.carrier) - at(b.carrier));
}

/**
 * 详情面交付员的工号行集（反馈_09 第 3／6 条：交付端订单详情「交付员信息」节的竖排口径）。
 *
 * 与账档读面 `readCarrierJobNos` 的差别两条：
 *   ① **整集为空数组时回退旧单值键**。账号档案那边子表空就该是空（删空＝清空），而详情面
 *      `carrierJobNos` 的三支各自有语义：非空数组＝子表在册的整集、`[]`＝子表为空、
 *      null／缺席＝该读腿未接（未知）——详情面上「子表空」与「这单只记了旧列那一枚」都得看得见，
 *      故 `[]` 在这里当「没有整集」处理。
 *   ② **字典外的枚举码不吞行**：展示层要出原码（见 `carrierTextOf`），所以本函数只校验
 *      「运营商码非空 ＋ 工号非空白」，不把值域收紧到四枚——后端枚举将来加一枚时，工号不会整行消失。
 *
 * 旧列回落行⛔ 在本函数里另抄一遍「旧列是电信口径」的假设：直接委托读面同一枚实现
 * （`readCarrierJobNos({ carrierJobNo })`），口径只有一个入口。
 * 整集非空时只信它：哪怕逐项校验后一行不剩（脏行）也不回退旧列冒充——子表在册时它就是权威读面。
 */
export function readCourierCarrierJobNoRows(source: unknown): CarrierJobNoTextRow[] {
  if (source === null || typeof source !== 'object') return [];
  const bag = source as { carrierJobNos?: unknown; carrierJobNo?: unknown };
  if (Array.isArray(bag.carrierJobNos) && bag.carrierJobNos.length > 0) {
    const rows: CarrierJobNoTextRow[] = [];
    for (const item of bag.carrierJobNos) {
      if (item === null || typeof item !== 'object') continue;
      const entry = item as { carrier?: unknown; jobNo?: unknown };
      const carrier = typeof entry.carrier === 'string' ? entry.carrier.trim() : '';
      const jobNo = carrierJobNoText(entry.jobNo);
      if (carrier === '' || jobNo === '') continue;
      rows.push({ carrier, jobNo });
    }
    return sortCarrierJobNoTextRows(rows);
  }
  return readCarrierJobNos({ carrierJobNo: bag.carrierJobNo }).map((row) => ({ carrier: row.carrier, jobNo: row.jobNo }));
}

/** 行集的一句话读面（弹窗回显当前值用）：空行集出空串，由调用方决定空态文案 */
export function carrierJobNosText(rows: readonly CarrierJobNoRow[]): string {
  return carrierJobNoSubmitRows(rows).map((row) => PRODUCT_CARRIER_META[row.carrier].label + ' ' + row.jobNo).join('、');
}

/** 同一运营商是否已被**别的行**占用（下拉置灰用；本行自己选中项不置灰） */
export function carrierTakenByOtherRow(rows: readonly CarrierJobNoRow[], carrier: ProductCarrier, index: number): boolean {
  return rows.some((row, i) => i !== index && row.carrier === carrier);
}

/** 本行是否还能再选某运营商（已用满四枚时新增按钮停用） */
export function carrierJobNoRowsFull(rows: readonly CarrierJobNoRow[]): boolean {
  return filledCarrierJobNoRows(rows).filter((row) => isProductCarrier(row.carrier)).length >= PRODUCT_CARRIER_ORDER.length;
}
